<template>
  <div class="sales-chart-container">
    
    <div class="sales-legend-container">
      <!-- Légende des typologies -->
      <div class="sales-legend-section">
        <div class="sales-legend-title">Typologies :</div>
        <div class="sales-legend-items">
          <div class="sales-legend-item" v-for="(color, type) in typeColors" :key="type">
            <div class="sales-legend-color" :style="{ backgroundColor: color }"></div>
            <span>{{ type.toUpperCase() }}</span>
          </div>
        </div>
      </div>
      
      <!-- Légende des formes -->
      <div class="sales-legend-section">
        <div class="sales-legend-title">TVA :</div>
        <div class="sales-legend-items">
          <div class="sales-legend-item">
            <div class="sales-legend-shape sales-legend-circle"></div>
            <span>TVA pleine</span>
          </div>
          <div class="sales-legend-item">
            <div class="sales-legend-shape sales-legend-triangle"></div>
            <span>TVA réduite</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Utilisation de Highcharts au lieu de Chart.js -->
    <highcharts 
      :options="chartOptions" 
      style="width: 100%; height: 350px;"
      ref="chart"
    ></highcharts>
    
  </div>
</template>

<script>
// Import Chart.js depuis Highcharts-vue déjà disponible dans votre projet
import { Chart } from 'highcharts-vue'

export default {
  name: 'SalesChart',
  props: {
    salesData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      chart: null,
      typeColors: {
        't1': '#3498db',
        't2': '#2ecc71',
        't3': '#f39c12',
        't4': '#e74c3c',
        't5': '#9b59b6'
      },
      stats: {
        totalSales: 0,
        avgPrice: 0,
        period: '-'
      },
      chartOptions: {
        chart: {
          type: 'scatter',
          height: 350
        },
        title: {
          text: null
        },
        credits: {
          enabled: false
        },
        legend: {
          enabled: false
        },
        xAxis: {
          categories: [],
          title: {
            text: ''
          },
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        yAxis: {
          title: {
            text: ''
          },
          labels: {
            formatter: function() {
              return this.value.toLocaleString('fr-FR') + ' €';
            }
          }
        },
        tooltip: {
          useHTML: true,
          formatter: function() {
            const point = this.point;
            const category = this.series.chart.xAxis[0].categories[point.x];
            const tvaType = this.series.name.includes('TVA 5.5%') ? 'TVA 5.5%' : 'TVA 20%';
            const shape = this.series.name.includes('TVA 5.5%') ? '▲' : '●';
            
            return `
              <div style="font-size: 12px;">
                <strong>${shape} ${this.series.name.replace(' (TVA 5.5%)', '')} - ${category}</strong><br/>
                Prix au m² : <strong>${point.y.toLocaleString('fr-FR')} €</strong><br/>
                TVA : <strong>${tvaType}</strong>
              </div>
            `;
          }
        },
        plotOptions: {
          scatter: {
            marker: {
              symbol: 'circle', // Forcer les ronds
              radius: 6,
              states: {
                hover: {
                  radius: 8
                }
              }
            }
          },
          series: {
            marker: {
              symbol: 'circle' // S'assurer que toutes les séries utilisent des ronds
            }
          }
        },
        series: []
      }
    }
  },
  computed: {
    processedData() {
      if (!this.salesData || !this.salesData.length) return [];
      
      return this.salesData.map(item => {
        const date = new Date(item.trimestre);
        const quarter = `T${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
        const pricePerSqm = item.prix_hors_parking / item.surface_habitable;
        
        return {
          category: quarter,
          y: Math.round(pricePerSqm),
          type: item.l_typo.toUpperCase(),
          surface: item.surface_habitable,
          price: item.prix_hors_parking,
          lotId: item.lot_id,
          trimestre: item.trimestre,
          isReducedTva: (item.l_tva == 'vr')
        };
      });
    },
    
    chartSeries() {
      if (!this.processedData.length) {
        return [];
      }
      
      // Grouper par typologie ET par catégorie
      const grouped = {};
      this.processedData.forEach(point => {
        if (!grouped[point.type]) {
          grouped[point.type] = {};
        }
        // Stocker toutes les ventes par catégorie (ne pas écraser)
        if (!grouped[point.type][point.category]) {
          grouped[point.type][point.category] = [];
        }
        grouped[point.type][point.category].push(point);
      });
      
      // Créer les séries avec tous les trimestres (même vides)
      const allTypologies = ['T1', 'T2', 'T3', 'T4', 'T5'];
      
      const series = allTypologies.map(type => {
        const dataCircles = []; // TVA pleine (cercles)
        const dataSquares = []; // TVA réduite (carrés)
        
        this.categories.forEach((category, index) => {
          if (grouped[type] && grouped[type][category] && grouped[type][category].length > 0) {
            // Il y a des ventes pour cette typologie à ce trimestre
            const sales = grouped[type][category];
            
            // Séparer selon le type de TVA
            sales.forEach(sale => {
              if (sale.isReducedTva) {
                dataSquares.push([index, sale.y]);
              } else {
                dataCircles.push([index, sale.y]);
              }
            });
          }
        });
        
        // Créer deux séries par typologie : une pour les cercles, une pour les carrés
        const seriesForType = [];
        
        // Série pour TVA pleine (cercles)
        if (dataCircles.length > 0) {
          seriesForType.push({
            name: type,
            color: this.typeColors[type.toLowerCase()] || '#CCCCCC',
            data: dataCircles,
            marker: {
              symbol: 'circle',
              radius: 6
            }
          });
        }
        
        // Série pour TVA réduite (triangles)
        if (dataSquares.length > 0) {
          seriesForType.push({
            name: `${type} (TVA 5.5%)`,
            color: this.typeColors[type.toLowerCase()] || '#CCCCCC',
            data: dataSquares,
            marker: {
              symbol: 'triangle',
              radius: 6
            }
          });
        }
        
        return seriesForType;
      }).flat(); // Aplatir le tableau car on a maintenant des sous-tableaux
      
      return series.filter(s => s.data.length > 0); // Ne garder que les séries avec des données
    },
    
    categories() {
      if (!this.processedData.length) return [];
      
      // Récupérer toutes les dates uniques et les trier
      const quarters = [...new Set(this.processedData.map(p => p.category))];
      const sortedQuarters = quarters.sort((a, b) => {
        const [qA, yearA] = a.split(' ');
        const [qB, yearB] = b.split(' ');
        if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
        return qA.localeCompare(qB);
      });
      
      // Générer tous les trimestres entre le premier et le dernier
      if (sortedQuarters.length < 2) return sortedQuarters;
      
      const [firstQ, firstYear] = sortedQuarters[0].split(' ');
      const [lastQ, lastYear] = sortedQuarters[sortedQuarters.length - 1].split(' ');
      
      const startYear = parseInt(firstYear);
      const endYear = parseInt(lastYear);
      const startQuarter = parseInt(firstQ.replace('T', ''));
      const endQuarter = parseInt(lastQ.replace('T', ''));
      
      const allQuarters = [];
      
      for (let year = startYear; year <= endYear; year++) {
        const startQ = year === startYear ? startQuarter : 1;
        const endQ = year === endYear ? endQuarter : 4;
        
        for (let quarter = startQ; quarter <= endQ; quarter++) {
          allQuarters.push(`T${quarter} ${year}`);
        }
      }
      
      return allQuarters;
    }
  },
  watch: {
    salesData: {
      handler() {
        this.updateChart();
        this.updateStats();
      },
      deep: true
    }
  },
  
  mounted() {
    this.updateChart();
    this.updateStats();
  },
  
  methods: {
    updateChart() {
      
      if (this.categories.length > 0 && this.chartSeries.length > 0) {
        // Mettre à jour directement les options
        this.chartOptions.xAxis.categories = this.categories;
        this.chartOptions.series = this.chartSeries;
      }
    },
    
    updateStats() {
      if (this.processedData.length > 0) {
        this.stats.totalSales = this.processedData.length;
        this.stats.avgPrice = Math.round(
          this.processedData.reduce((sum, p) => sum + p.y, 0) / this.processedData.length
        );
        const dates = this.processedData.map(p => p.category).sort();
        this.stats.period = dates.length > 1 ? `${dates[0]} - ${dates[dates.length - 1]}` : dates[0] || '-';
      } else {
        this.stats = { totalSales: 0, avgPrice: 0, period: '-' };
      }
      
      this.$emit('data-updated', this.stats);
    },
    
    formatPrice(price) {
      if (price === 0) return '-';
      return price.toLocaleString('fr-FR') + ' €/m²';
    }
  },
  // eslint-disable-next-line
  components: { highcharts: Chart }
}
</script>

<style scoped>
.sales-chart-container {
  background: white;
  border-radius: 8px;
  width: 100%;
}

.sales-chart-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 15px;
  text-align: center;
}

.sales-legend-container {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 30px;
  margin-bottom: 10px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.sales-legend-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sales-legend-title {
  font-weight: 600;
  color: #2c3e50;
  font-size: 13px;
}

.sales-legend-items {
  display: flex;
  gap: 15px;
}

.sales-legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  background: white;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.sales-legend-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.sales-legend-shape {
  width: 12px;
  height: 12px;
  background-color: #6c757d;
}

.sales-legend-circle {
  border-radius: 50%;
}

.sales-legend-square {
  border-radius: 2px;
}

.sales-legend-triangle {
  width: 0;
  height: 0;
  background-color: transparent !important;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 10px solid #6c757d;
}

.sales-stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 20px;
}

.sales-stat-card {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  border-left: 4px solid #3498db;
  text-align: center;
}

.sales-stat-title {
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 5px;
}

.sales-stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}
</style>
